import { type Nullable } from "core/types";
import { type Observer } from "core/Misc/observable";
import { Vector3, Matrix } from "core/Maths/math.vector.pure";
import { Tools } from "core/Misc/tools.pure";
import { type Scene } from "core/scene";

import { Control } from "./control.pure";
import { ValueAndUnit } from "../valueAndUnit";
import { type Measure } from "../measure";
import { RegisterClass } from "core/Misc/typeStore";
import { serialize } from "core/Misc/decorators";
import { type ICanvasRenderingContext } from "core/Engines/ICanvas";

/** Class used to render 2D lines */
export class Line extends Control {
    private _lineWidth = 1;
    /** @internal */
    public _x1 = new ValueAndUnit(0);
    /** @internal */
    public _y1 = new ValueAndUnit(0);
    /** @internal */
    public _x2 = new ValueAndUnit(0);
    /** @internal */
    public _y2 = new ValueAndUnit(0);
    private _dash = new Array<number>();
    private _connectedControl: Control;
    private _connectedControlDirtyObserver: Nullable<Observer<Control>>;

    /** Gets or sets the dash pattern */
    @serialize()
    public get dash(): Array<number> {
        return this._dash;
    }

    public set dash(value: Array<number>) {
        if (this._dash === value) {
            return;
        }

        this._dash = value;
        this._markAsDirty();
    }

    /** Gets or sets the control connected with the line end */
    public get connectedControl(): Control {
        return this._connectedControl;
    }

    public set connectedControl(value: Control) {
        // Don't allow circular references, and no-op if the same control is assigned
        if (this._connectedControl === value || (value instanceof Line && value._connectedControl === (this as Control))) {
            return;
        }

        if (this._connectedControlDirtyObserver && this._connectedControl) {
            this._connectedControl.onDirtyObservable.remove(this._connectedControlDirtyObserver);
            this._connectedControlDirtyObserver = null;
        }

        if (value) {
            this._connectedControlDirtyObserver = value.onDirtyObservable.add(() => this._markAsDirty());
        }

        this._connectedControl = value;
        this._markAsDirty();
    }

    /** Gets or sets start coordinates on X axis */
    @serialize()
    public get x1(): string | number {
        return this._x1.toString(this._host);
    }

    public set x1(value: string | number) {
        if (this._x1.toString(this._host) === value) {
            return;
        }

        if (this._x1.fromString(value)) {
            this._markAsDirty();
        }
    }

    /** Gets or sets start coordinates on Y axis */
    @serialize()
    public get y1(): string | number {
        return this._y1.toString(this._host);
    }

    public set y1(value: string | number) {
        if (this._y1.toString(this._host) === value) {
            return;
        }

        if (this._y1.fromString(value)) {
            this._markAsDirty();
        }
    }

    /** Gets or sets end coordinates on X axis */
    @serialize()
    public get x2(): string | number {
        return this._x2.toString(this._host);
    }

    public set x2(value: string | number) {
        if (this._x2.toString(this._host) === value) {
            return;
        }

        if (this._x2.fromString(value)) {
            this._markAsDirty();
        }
    }

    /** Gets or sets end coordinates on Y axis */
    @serialize()
    public get y2(): string | number {
        return this._y2.toString(this._host);
    }

    public set y2(value: string | number) {
        if (this._y2.toString(this._host) === value) {
            return;
        }

        if (this._y2.fromString(value)) {
            this._markAsDirty();
        }
    }

    /** Gets or sets line width */
    @serialize()
    public get lineWidth(): number {
        return this._lineWidth;
    }

    public set lineWidth(value: number) {
        if (this._lineWidth === value) {
            return;
        }

        this._lineWidth = value;
        this._markAsDirty();
    }

    /** Gets or sets horizontal alignment */
    public override set horizontalAlignment(value: number) {
        return;
    }

    /** Gets or sets vertical alignment */
    public override set verticalAlignment(value: number) {
        return;
    }

    /** @internal */
    public get _effectiveX2(): number {
        return (this._connectedControl ? this._connectedControl.centerX - this._cachedParentMeasure.left : 0) + this._getValueInPixel(this._x2, 1);
    }

    /** @internal */
    public get _effectiveY2(): number {
        return (this._connectedControl ? this._connectedControl.centerY - this._cachedParentMeasure.top : 0) + this._getValueInPixel(this._y2, 1);
    }

    /**
     * Creates a new Line
     * @param name defines the control name
     */
    constructor(public override name?: string) {
        super(name);

        this._automaticSize = true;

        this.isHitTestVisible = false;
        this._horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        this._verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
    }

    protected override _getTypeName(): string {
        return "Line";
    }

    public override _draw(context: ICanvasRenderingContext): void {
        context.save();

        if (this.shadowBlur || this.shadowOffsetX || this.shadowOffsetY) {
            context.shadowColor = this.shadowColor;
            context.shadowBlur = this.shadowBlur;
            context.shadowOffsetX = this.shadowOffsetX * this._host.idealRatio;
            context.shadowOffsetY = this.shadowOffsetY * this._host.idealRatio;
        }

        this._applyStates(context);
        context.strokeStyle = this._getColor(context);
        context.lineWidth = this._lineWidth;
        context.setLineDash(this._dash);

        context.beginPath();
        context.moveTo(this._cachedParentMeasure.left + this._getValueInPixel(this._x1, 1), this._cachedParentMeasure.top + this._getValueInPixel(this._y1, 1));

        context.lineTo(this._cachedParentMeasure.left + this._effectiveX2, this._cachedParentMeasure.top + this._effectiveY2);
        context.stroke();

        context.restore();
    }

    public override _measure(): void {
        // Width / Height
        this._currentMeasure.width = Math.abs(this._getValueInPixel(this._x1, 1) - this._effectiveX2) + this._lineWidth;
        this._currentMeasure.height = Math.abs(this._getValueInPixel(this._y1, 1) - this._effectiveY2) + this._lineWidth;
    }

    protected override _preMeasure(parentMeasure: Measure): void {
        this._cachedParentMeasure.copyFrom(parentMeasure);
    }

    public override _layout(parentMeasure: Measure, context: ICanvasRenderingContext): boolean {
        if (this._connectedControl) {
            // If we have a connected control, we need to let it layout first so we can ensure our layout math uses its latest position
            this._connectedControl._layout(parentMeasure, context);
        }
        return super._layout(parentMeasure, context);
    }

    protected override _computeAlignment(parentMeasure: Measure): void {
        this._currentMeasure.left = parentMeasure.left + Math.min(this._getValueInPixel(this._x1, 1), this._effectiveX2) - this._lineWidth / 2;
        this._currentMeasure.top = parentMeasure.top + Math.min(this._getValueInPixel(this._y1, 1), this._effectiveY2) - this._lineWidth / 2;
    }

    /**
     * Move one end of the line given 3D cartesian coordinates.
     * @param position Targeted world position
     * @param scene Scene
     * @param end (opt) Set to true to assign x2 and y2 coordinates of the line. Default assign to x1 and y1.
     */
    public override moveToVector3(position: Vector3, scene: Scene, end: boolean = false): void {
        if (!this._host || this.parent !== this._host._rootContainer) {
            Tools.Error("Cannot move a control to a vector3 if the control is not at root level");
            return;
        }

        const globalViewport = this._host._getGlobalViewport();
        const projectedPosition = Vector3.Project(position, Matrix.IdentityReadOnly, scene.getTransformMatrix(), globalViewport);

        this._moveToProjectedPosition(projectedPosition, end);

        if (projectedPosition.z < 0 || projectedPosition.z > 1) {
            this.notRenderable = true;
            return;
        }
        this.notRenderable = false;
    }

    /**
     * Move one end of the line to a position in screen absolute space.
     * @param projectedPosition Position in screen absolute space (X, Y)
     * @param end (opt) Set to true to assign x2 and y2 coordinates of the line. Default assign to x1 and y1.
     */
    public override _moveToProjectedPosition(projectedPosition: Vector3, end: boolean = false): void {
        const x: string = projectedPosition.x + this._getValueInPixel(this._linkOffsetX, 1) + "px";
        const y: string = projectedPosition.y + this._getValueInPixel(this._linkOffsetY, 1) + "px";

        if (end) {
            this.x2 = x;
            this.y2 = y;
            this._x2.ignoreAdaptiveScaling = true;
            this._y2.ignoreAdaptiveScaling = true;
        } else {
            this.x1 = x;
            this.y1 = y;
            this._x1.ignoreAdaptiveScaling = true;
            this._y1.ignoreAdaptiveScaling = true;
        }
    }
}

let _Registered = false;
/**
 * Registers the Line class with the type store for serialization support.
 * Safe to call multiple times; only the first call has an effect.
 */
export function RegisterLine(): void {
    if (_Registered) {
        return;
    }
    _Registered = true;

    RegisterClass("BABYLON.GUI.Line", Line);
}
